import i18n from '../../../i18n';
import { parseAuthSessionPayload } from '../../../shared/services/authRefresh';
import type { ClientAuthSession } from '../../../shared/services/authSession';
import type {
  CreateWorkspaceResult,
  GlobalLoginResult,
  PlatformAccount,
  SignUpResult,
  SlugAvailability,
  WorkspaceSelection,
  WorkspaceSummary,
} from '../types/workspace';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

const PICKER_SESSION_KEY = 'workspacePickerSession';

export interface StoredPickerSession {
  token: string;
  expiresAt: string;
  username: string;
  workspaces: WorkspaceSummary[];
  /** Absent for legacy members, whose picker can list workspaces but not create one. */
  accountToken?: string | null;
}

const readErrorMessage = (payload: any, fallback: string): string =>
  payload?.error?.message
  || payload?.Error?.Message
  || payload?.error?.details?.[0]?.issue
  || payload?.Error?.Details?.[0]?.Issue
  || payload?.title
  || payload?.Title
  || payload?.message
  || payload?.Message
  || fallback;

const throwHttpError = (response: Response, payload: any, fallback: string): never => {
  const fromBody = readErrorMessage(payload, '');
  if (fromBody) {
    throw new Error(fromBody);
  }

  throw new Error(`${fallback} (${response.status})`);
};

const parseJson = async (response: Response): Promise<any> => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const toWorkspace = (value: any): WorkspaceSummary => ({
  tenantId: String(value?.tenantId ?? ''),
  slug: String(value?.slug ?? ''),
  name: String(value?.name ?? ''),
  url: String(value?.url ?? ''),
  requiresPasswordChange: Boolean(value?.requiresPasswordChange),
});

const toAccount = (value: any): PlatformAccount | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  return {
    userId: String(value.userId ?? ''),
    email: String(value.email ?? ''),
    displayName: value.displayName ? String(value.displayName) : null,
    emailVerified: Boolean(value.emailVerified),
  };
};

const toSelection = (value: any): WorkspaceSelection | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const redirectUrl = String(value.redirectUrl ?? '');
  if (!redirectUrl) {
    return null;
  }

  return {
    tenantId: String(value.tenantId ?? ''),
    slug: String(value.slug ?? ''),
    url: String(value.url ?? ''),
    redirectUrl,
    handoffCode: value.handoffCode ? String(value.handoffCode) : null,
    handoffExpiresAtUtc: value.handoffExpiresAtUtc ? String(value.handoffExpiresAtUtc) : null,
  };
};

/**
 * Global login runs before any workspace is known, so it deliberately sends no tenant headers.
 */
export const globalLogin = async (username: string, password: string): Promise<GlobalLoginResult> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/auth/global/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throwHttpError(response, payload, i18n.t('workspaces:errors.loginFailed'));
  }

  return {
    pickerToken: String(payload?.pickerToken ?? ''),
    pickerExpiresAtUtc: String(payload?.pickerExpiresAtUtc ?? ''),
    workspaces: Array.isArray(payload?.workspaces) ? payload.workspaces.map(toWorkspace) : [],
    selection: toSelection(payload?.selection),
    accountToken: payload?.accountToken ? String(payload.accountToken) : null,
    accountExpiresAtUtc: payload?.accountExpiresAtUtc ? String(payload.accountExpiresAtUtc) : null,
    account: toAccount(payload?.account),
  };
};

/** Creates the Simaizeer account itself. The first workspace is a separate, later step. */
export const globalSignUp = async (
  email: string,
  password: string,
  displayName: string,
): Promise<SignUpResult> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/auth/global/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, displayName }),
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throwHttpError(response, payload, i18n.t('workspaces:errors.signUpFailed'));
  }

  const account = toAccount(payload?.account);
  if (!account) {
    throw new Error(i18n.t('workspaces:errors.signUpFailed'));
  }

  return {
    accountToken: String(payload?.accountToken ?? ''),
    accountExpiresAtUtc: String(payload?.accountExpiresAtUtc ?? ''),
    account,
    canCreateWorkspace: Boolean(payload?.canCreateWorkspace),
  };
};

export const checkWorkspaceSlug = async (
  accountToken: string,
  slug: string,
): Promise<SlugAvailability> => {
  const query = new URLSearchParams({ slug, accountToken });

  const response = await fetch(
    `${API_BASE_URL}/api/v1/auth/global/workspaces/slug-available?${query.toString()}`,
    { method: 'GET' },
  );

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(readErrorMessage(payload, i18n.t('workspaces:errors.slugCheckFailed')));
  }

  return {
    slug: String(payload?.slug ?? slug),
    available: Boolean(payload?.available),
    reason: payload?.reason ? String(payload.reason) : null,
  };
};

export const createWorkspace = async (
  accountToken: string,
  name: string,
  slug: string,
): Promise<CreateWorkspaceResult> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/auth/global/workspaces`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ accountToken, name, slug }),
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(readErrorMessage(payload, i18n.t('workspaces:errors.createFailed')));
  }

  const selection = toSelection(payload);
  if (!selection) {
    throw new Error(i18n.t('workspaces:errors.createFailed'));
  }

  return { ...selection, name: String(payload?.name ?? '') };
};

export const selectWorkspace = async (pickerToken: string, tenantId: string): Promise<WorkspaceSelection> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/auth/global/workspaces/select`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pickerToken, tenantId }),
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(readErrorMessage(payload, i18n.t('workspaces:errors.selectFailed')));
  }

  const selection = toSelection(payload);
  if (!selection) {
    throw new Error(i18n.t('workspaces:errors.selectFailed'));
  }

  return selection;
};

/**
 * Redeems the one-time code on the workspace host. `tenantId` is only needed in local development,
 * where the origin is localhost and cannot identify the workspace on its own.
 */
export const consumeWorkspaceHandoff = async (
  code: string,
  tenantId?: string | null,
): Promise<ClientAuthSession> => {
  const trimmedTenantId = tenantId?.trim();

  const response = await fetch(`${API_BASE_URL}/api/v1/auth/handoff/consume`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Tenant-Url': window.location.origin,
      ...(trimmedTenantId ? { TenantId: trimmedTenantId, 'X-Tenant-Id': trimmedTenantId } : {}),
    },
    body: JSON.stringify({ code }),
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(readErrorMessage(payload, i18n.t('workspaces:errors.handoffFailed')));
  }

  return parseAuthSessionPayload(payload, trimmedTenantId);
};

export const savePickerSession = (session: StoredPickerSession): void => {
  try {
    sessionStorage.setItem(PICKER_SESSION_KEY, JSON.stringify(session));
  } catch {
    // Ignore: the picker still works for the current navigation.
  }
};

export const readPickerSession = (): StoredPickerSession | null => {
  try {
    const raw = sessionStorage.getItem(PICKER_SESSION_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as StoredPickerSession;
    if ((!parsed?.token && !parsed?.accountToken) || !Array.isArray(parsed.workspaces)) {
      return null;
    }

    if (parsed.expiresAt && Date.parse(parsed.expiresAt) <= Date.now()) {
      clearPickerSession();
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

export const clearPickerSession = (): void => {
  try {
    sessionStorage.removeItem(PICKER_SESSION_KEY);
  } catch {
    // Ignore.
  }
};
